import { tabService, type SecurityFinding, type TabSecurity } from '@/services/tabs';

export type TabSecurityStatus = 'safe' | 'warning' | 'danger' | 'unscanned';

export interface TabSecurityInfo {
  /** null until the tab has been scanned by the backend. */
  score: number | null;
  status: TabSecurityStatus;
  threats: string[];
  findings: SecurityFinding[];
  recommendations: string[];
  /** Derived from the URL scheme, which is a fact rather than a scan result. */
  encryption: boolean;
  lastScanned: string;
}

export interface TabForSecurityScan {
  id: string;
  url: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SCAN_CONCURRENCY = 6;
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  url: string;
  info: TabSecurityInfo;
  at: number;
}

// Shared across components so TabMonitor, its alerts panel and the
// intelligence dashboard do not each re-scan the same tabs.
const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<TabSecurityInfo>>();

/** Only tabs persisted by the backend (UUID ids) can be scanned. */
export function isScannableTabId(id: string): boolean {
  return UUID_RE.test(id);
}

export function isEncryptedUrl(url: string): boolean {
  return /^(https|wss):\/\//i.test(url);
}

/** Placeholder for a tab that has not been scanned: no score is invented. */
export function unscannedTabSecurity(url: string, lastScanned = 'Not yet scanned'): TabSecurityInfo {
  return {
    score: null,
    status: 'unscanned',
    threats: [],
    findings: [],
    recommendations: [],
    encryption: isEncryptedUrl(url),
    lastScanned,
  };
}

function formatScanTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function tabSecurityFromScan(scan: TabSecurity, url: string): TabSecurityInfo {
  return {
    score: scan.score,
    status: scan.status,
    threats: scan.threats,
    findings: scan.findings,
    recommendations: scan.recommendations,
    encryption: scan.url !== undefined ? scan.encryption : isEncryptedUrl(url),
    lastScanned: formatScanTime(scan.lastScanned),
  };
}

/** Returns a cached scan result for the tab if it is fresh and the URL is unchanged. */
export function getCachedTabSecurity(id: string, url: string): TabSecurityInfo | undefined {
  const entry = cache.get(id);
  if (!entry || entry.url !== url || Date.now() - entry.at > CACHE_TTL_MS) return undefined;
  return entry.info;
}

export function clearTabSecurityCache(): void {
  cache.clear();
  inFlight.clear();
}

async function scanOne(tab: TabForSecurityScan, force: boolean): Promise<TabSecurityInfo> {
  if (!isScannableTabId(tab.id)) {
    return unscannedTabSecurity(tab.url);
  }
  if (!force) {
    const cached = getCachedTabSecurity(tab.id, tab.url);
    if (cached) return cached;
  }
  const key = `${tab.id}|${tab.url}`;
  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = tabService
    .scanTabSecurity(tab.id)
    .then((scan) => {
      const info = tabSecurityFromScan(scan, tab.url);
      cache.set(tab.id, { url: tab.url, info, at: Date.now() });
      return info;
    })
    .catch(() => unscannedTabSecurity(tab.url, 'Scan failed'))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
}

/**
 * Scans tabs through POST /security/tabs/:id/scan with bounded concurrency.
 * Results for tabs that cannot be scanned have status 'unscanned'.
 * `force` bypasses the cache (e.g. for an explicit "Scan all").
 */
export async function scanTabsSecurity(
  tabs: TabForSecurityScan[],
  opts?: { force?: boolean },
): Promise<Map<string, TabSecurityInfo>> {
  const force = Boolean(opts?.force);
  const results = new Map<string, TabSecurityInfo>();
  let next = 0;
  const worker = async () => {
    while (next < tabs.length) {
      const tab = tabs[next++];
      results.set(tab.id, await scanOne(tab, force));
    }
  };
  await Promise.all(Array.from({ length: Math.min(SCAN_CONCURRENCY, tabs.length) }, worker));
  return results;
}

/** Number of results that came back as failed scans (not merely unscannable ids). */
export function countFailedScans(results: Map<string, TabSecurityInfo>): number {
  let n = 0;
  for (const info of results.values()) {
    if (info.status === 'unscanned' && info.lastScanned === 'Scan failed') n++;
  }
  return n;
}
